if ( typeof InteractiveMap !== 'function' ) {

  class InteractiveMap extends HTMLElement {

    constructor(){

      super();

      this.map = null;
      this.markers = {};
      this.markerElements = [];

      let initTimer,
          initObserver;

      initTimer = setTimeout(()=>{
        this.#init();
        initObserver.unobserve(this);
      }, 10000);

      initObserver = new IntersectionObserver((entries, observer) => {
        if ( ! entries[0].isIntersecting ) {
          return;
        } else {
          this.#init();
          clearInterval(initTimer);
          observer.unobserve(this);
        }
      });
      initObserver.observe(this);

    }

    #init(){

      if ( this.map || this.initializing ) {
        return;
      }

      this.initializing = true;

      this.#loadMapLibre()
        .then(()=>{
          this.#createMap();
        })
        .catch(()=>{
          this.fallback();
        })
        .finally(()=>{
          this.initializing = false;
        });

    }

    init(){
      this.#init();
    }

    #loadMapLibre(){

      if ( window.maplibregl ) {
        return Promise.resolve();
      }

      if ( window.loadingMapLibreScript ) {
        return window.loadingMapLibreScript;
      }

      window.loadingMapLibreScript = new Promise((resolve, reject)=>{

        if ( ! document.getElementById('maplibre-gl-css') ) {
          const mapLibreStyles = document.createElement('link');
          mapLibreStyles.id = 'maplibre-gl-css';
          mapLibreStyles.rel = 'stylesheet';
          mapLibreStyles.href = 'https://unpkg.com/maplibre-gl@5.19.0/dist/maplibre-gl.css';
          document.head.appendChild(mapLibreStyles);
        }

        const mapLibreScript = document.createElement('script');
        mapLibreScript.onload = resolve;
        mapLibreScript.onerror = reject;
        mapLibreScript.async = true;
        mapLibreScript.src = 'https://unpkg.com/maplibre-gl@5.19.0/dist/maplibre-gl.js';
        document.body.appendChild(mapLibreScript);

      });

      return window.loadingMapLibreScript;

    }

    #createMap(){

      const mapEl = this.querySelector('[data-js-map-object]');
      const mapMarkers = this.querySelectorAll('[data-js-map-addresses] li');
      const requestedZoom = parseInt(this.dataset.zoom);
      const zoom = Number.isFinite(requestedZoom) ? requestedZoom : 16;
      const useTagPin = this.hasAttribute('data-tag-pin');
      const styleUrl = this.#getStyleUrl();
      const validMarkers = [];

      mapMarkers.forEach((elm, i)=>{
        const latitude = parseFloat(elm.dataset.latitude);
        const longitude = parseFloat(elm.dataset.longitude);

        if ( Number.isFinite(latitude) && Number.isFinite(longitude) ) {
          validMarkers.push({
            id: elm.dataset.id,
            title: elm.dataset.title || '',
            latitude,
            longitude,
            marker: elm.dataset.marker,
            markerId: i
          });
        }
      });

      if ( ! mapEl || validMarkers.length === 0 ) {
        this.fallback();
        return;
      }

      const firstMarker = validMarkers[0];

      this.map = new maplibregl.Map({
        container: mapEl.id,
        style: styleUrl,
        center: [firstMarker.longitude, firstMarker.latitude],
        zoom: zoom,
        minZoom: 2,
        maxZoom: 19,
        scrollZoom: false,
        attributionControl: true
      });

      this.map.addControl(new maplibregl.NavigationControl({
        showCompass: false
      }), 'top-right');

      this.map.once('load', ()=>{
        const bounds = new maplibregl.LngLatBounds();

        validMarkers.forEach(markerData=>{
          const markerElement = this.#createMarkerElement(markerData, useTagPin);
          const marker = new maplibregl.Marker({
            element: markerElement,
            anchor: useTagPin ? 'bottom' : 'center'
          })
            .setLngLat([markerData.longitude, markerData.latitude])
            .addTo(this.map);

          const triggerMarker = (options = {}) => {
            this.#activateMarker(markerData.id);

            if ( ! options.focusOnly ) {
              this.dispatchEvent(new CustomEvent("marker", {
                detail: {
                  store: markerData.id
                }
              }));
            }

            this.map.panTo([markerData.longitude, markerData.latitude], {
              duration: 300
            });
          };

          markerElement.addEventListener('click', triggerMarker);
          markerElement.addEventListener('keyup', e=>{
            if ( e.keyCode === 13 || (window.KEYCODES && e.keyCode === window.KEYCODES.RETURN) ) {
              triggerMarker();
            }
          });

          this.markers[markerData.id] = {
            marker,
            element: markerElement,
            lngLat: [markerData.longitude, markerData.latitude],
            trigger: triggerMarker
          };
          this.markerElements.push(markerElement);
          bounds.extend([markerData.longitude, markerData.latitude]);
        });

        if ( validMarkers.length > 1 ) {
          this.map.fitBounds(bounds, {
            padding: 40,
            maxZoom: zoom,
            duration: 0
          });
        } else {
          this.map.setZoom(zoom);
        }

        this.#bindResizeHandlers();
        this.#resize();
        this.dispatchEvent(new CustomEvent("ready"));
      });

      this.map.once('error', ()=>{
        if ( this.map && ! this.map.loaded() ) {
          this.fallback();
        }
      });

    }

    #getStyleUrl(){
      const styleUrls = {
        positron: 'https://tiles.openfreemap.org/styles/positron',
        bright: 'https://tiles.openfreemap.org/styles/bright',
        liberty: 'https://tiles.openfreemap.org/styles/liberty',
        dark: 'https://tiles.openfreemap.org/styles/dark',
        fiord: 'https://tiles.openfreemap.org/styles/fiord'
      };

      return styleUrls[this.dataset.style] || styleUrls.positron;
    }

    #createMarkerElement(markerData, useTagPin){
      let markerElement;

      if ( useTagPin ) {
        markerElement = document.createElement('div');
        markerElement.className = 'map-tag-pin';
        markerElement.textContent = markerData.title;
      } else {
        const markerImage = document.createElement('img');
        markerImage.src = `${markerData.marker}&id=${markerData.markerId}-${this.id}`;
        markerImage.width = 30;
        markerImage.height = 30;
        markerImage.alt = markerData.title;

        markerElement = document.createElement('div');
        markerElement.className = 'map-image-pin';
        markerElement.appendChild(markerImage);
      }

      markerElement.tabIndex = 0;
      markerElement.setAttribute('role', 'button');
      markerElement.setAttribute('aria-label', markerData.title);

      return markerElement;
    }

    #activateMarker(markerId){
      this.markerElements.forEach(el=>{
        el.classList.remove('map-tag-pin--active');
        el.style.zIndex = 0;
        const markerImage = el.querySelector('img');
        if ( markerImage ) {
          markerImage.classList.remove('scaled-store-icon');
        }
      });

      const marker = this.markers[markerId];
      if ( marker ) {
        marker.element.style.zIndex = 2;
        if ( marker.element.classList.contains('map-tag-pin') ) {
          marker.element.classList.add('map-tag-pin--active');
        } else {
          const markerImage = marker.element.querySelector('img');
          if ( markerImage ) {
            markerImage.classList.add('scaled-store-icon');
          }
        }
      }
    }

    #bindResizeHandlers(){
      if ( this.resizeHandlersBound ) {
        return;
      }

      this.resizeHandlersBound = true;
      this.resizeHandler = () => this.#resize();
      window.addEventListener('resize', this.resizeHandler);

      const modal = this.closest('modal-box');
      if ( modal ) {
        this.modalObserver = new MutationObserver(()=>{
          this.#resize();
        });
        this.modalObserver.observe(modal, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    }

    #resize(){
      if ( ! this.map ) {
        return;
      }

      requestAnimationFrame(()=>{
        this.map.resize();
        setTimeout(()=>{
          this.map.resize();
        }, 350);
      });
    }

    triggerMarkerClick(markerId, options = {}){
      const marker = this.markers[markerId];
      if ( marker ) {
        marker.trigger(options);
      }
    }

    disconnectedCallback(){
      if ( this.ti ) {
        clearInterval(this.ti);
      }
      if ( this.resizeHandler ) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      if ( this.modalObserver ) {
        this.modalObserver.disconnect();
      }
      if ( this.map ) {
        this.map.remove();
      }
    }

    fallback(){
      const fallback = this.querySelector('[data-js-map-fallback]');
      const mapObject = this.querySelector('[data-js-map-object]');
      const loader = this.querySelector('.map-loader');

      if ( fallback ) {
        fallback.style.display = "block";
      }
      if ( mapObject ) {
        mapObject.style.display = "none";
      }
      if ( loader ) {
        loader.style.display = "none";
      }
    }

  }

  if ( typeof customElements.get('interactive-map') === 'undefined' ) {
    customElements.define('interactive-map', InteractiveMap);
  }

}
